import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Users, Eye, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SendCampaign = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [formData, setFormData] = useState({
    campaign_name: '',
    campaign_type: '',
    template_id: '',
    subject: '',
    google_drive_link: '',
    magazine: 'all',
    type: 'member',
    status: 'Active',
    expiry_days: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (formData.template_id) {
      updateRecipientCount();
    }
  }, [formData.magazine, formData.type, formData.status, formData.expiry_days]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch templates');
    }
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        ...formData,
        template_id: templateId,
        campaign_type: template.template_type,
        subject: template.subject,
        campaign_name: `${template.template_name} - ${new Date().toLocaleDateString()}`
      });

      // Set expiry_days if it's an expiry reminder template
      if (template.template_type === 'expiry_30_days') {
        setFormData(prev => ({ ...prev, expiry_days: '30' }));
      } else if (template.template_type === 'expiry_15_days') {
        setFormData(prev => ({ ...prev, expiry_days: '15' }));
      } else if (template.template_type === 'expiry_7_days') {
        setFormData(prev => ({ ...prev, expiry_days: '7' }));
      }
    }
  };

  const updateRecipientCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const filters = {
        magazine: formData.magazine,
        type: formData.type,
        status: formData.status,
        expiry_days: formData.expiry_days
      };
      
      const response = await axios.post(`${API}/email/recipients/count`, filters, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRecipientCount(response.data.count);
    } catch (error) {
      console.error('Error getting recipient count:', error);
    }
  };

  const handleSendCampaign = async () => {
    if (!formData.campaign_name || !formData.template_id) {
      toast.error('Please fill all required fields');
      return;
    }

    if (recipientCount === 0) {
      toast.error('No recipients found with selected filters');
      return;
    }

    if (formData.campaign_type === 'monthly_magazine' && !formData.google_drive_link) {
      toast.error('Please provide Google Drive link for magazine');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        campaign_name: formData.campaign_name,
        campaign_type: formData.campaign_type,
        subject: formData.subject,
        template_id: formData.template_id,
        google_drive_link: formData.google_drive_link,
        recipient_filters: {
          magazine: formData.magazine,
          type: formData.type,
          status: formData.status,
          expiry_days: formData.expiry_days
        }
      };

      const response = await axios.post(`${API}/email/campaigns/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Campaign sent successfully! ${response.data.sent_count} emails sent.`);
      navigate('/campaign-reports');
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
              📨 Send Email Campaign
            </h1>
          </div>
        </div>

        {/* Recipient Count Card */}
        <div className="mb-6 p-6 rounded-lg border-2 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="text-blue-600" size={32} />
              <div>
                <p className="text-sm text-blue-600">Recipients Selected</p>
                <p className="text-3xl font-bold text-blue-800">{recipientCount}</p>
              </div>
            </div>
            <Button onClick={updateRecipientCount} variant="outline" size="sm">
              Refresh Count
            </Button>
          </div>
        </div>

        <div className="form-section">
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#7c3aed' }}>Campaign Details</h2>

          <div className="space-y-6">
            {/* Template Selection */}
            <div className="space-y-2">
              <Label>Select Email Template *</Label>
              <Select value={formData.template_id} onValueChange={handleTemplateChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.template_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Campaign Name */}
            <div className="space-y-2">
              <Label>Campaign Name *</Label>
              <Input
                value={formData.campaign_name}
                onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
                placeholder="E.g., January 2025 Magazine Distribution"
              />
            </div>

            {/* Email Subject */}
            <div className="space-y-2">
              <Label>Email Subject *</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Email subject line"
              />
            </div>

            {/* Google Drive Link (for magazine campaigns) */}
            {formData.campaign_type === 'monthly_magazine' && (
              <div className="space-y-2">
                <Label>Google Drive Magazine Link *</Label>
                <Input
                  value={formData.google_drive_link}
                  onChange={(e) => setFormData({ ...formData, google_drive_link: e.target.value })}
                  placeholder="Paste Google Drive shareable link"
                />
                <p className="text-xs text-gray-500">Make sure the link is publicly accessible</p>
              </div>
            )}
          </div>
        </div>

        {/* Recipient Filters */}
        <div className="form-section mt-6">
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#7c3aed' }}>Filter Recipients</h2>

          <div className="grid grid-cols-2 gap-6">
            {/* Magazine Filter */}
            <div className="space-y-2">
              <Label>Magazine</Label>
              <Select value={formData.magazine} onValueChange={(value) => setFormData({ ...formData, magazine: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Magazines</SelectItem>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Type Filter */}
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Members</SelectItem>
                  <SelectItem value="advertiser">Advertisers</SelectItem>
                  <SelectItem value="all">Everyone (Members + Advertisers)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Complimentary">Complimentary</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end space-x-4">
          <Button variant="outline" onClick={() => navigate('/')}>
            Cancel
          </Button>
          <Button
            onClick={handleSendCampaign}
            disabled={loading || recipientCount === 0}
            style={{ backgroundColor: '#7c3aed' }}
          >
            <Send className="mr-2" size={18} />
            {loading ? 'Sending...' : `Send to ${recipientCount} Recipients`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendCampaign;